import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { MatProgressBar } from '@angular/material/progress-bar'
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog'
import {
  MatCard,
  MatCardHeader,
  MatCardModule,
  MatCardTitle,
} from '@angular/material/card'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { MatTableModule } from '@angular/material/table'
import { forkJoin, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { ProposalItemService } from '../../../../core/services/proposal/proposal-item/proposal-item.service'
import { ItinerariesService } from '../../../../core/services/itineraries/itineraries/itiniraries.service'
import { SnackbarCustomService } from '../../../../shared/components/snackbar/snackbar-custom.service'
import { convertCurrency } from '../../../../shared/utils/utils'
import { ModalPurchasesDiscountApplyComponent } from '../modal-purchases-discount-apply/modal-purchases-discount-apply.component'
import { ModalPurchasesDiscountItinerariesComponent } from '../modal-purchases-discount-itineraries/modal-purchases-discount-itineraries.component'

@Component({
  selector: 'purchases-discount-tg-view',
  standalone: true,
  imports: [
    MatProgressBar,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardModule,
    MatIcon,
    MatTooltip,
    MatTableModule,
  ],
  templateUrl: './purchases-discount-tg-view.component.html',
  styleUrl: './purchases-discount-tg-view.component.scss',
})
export class PurchasesDiscountTgViewComponent implements OnInit {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PurchasesDiscountTgViewComponent>,
    private dialog: MatDialog,
    private proposalItemService: ProposalItemService,
    private itinerariesService: ItinerariesService,
    private snackbar: SnackbarCustomService,
  ) {
    this.item = data ?? {}
  }

  item: any = {}
  loading = false
  changed = false
  rows: any[] = []

  @Output() response = new EventEmitter()

  columns: any[] = [
    {
      keyColumn: 'location_OD',
      key: 'origem',
      description: 'Trecho / Itinerário',
    },
    { keyColumn: 'servico_TS', key: 'descricaoServico', description: 'Serviço' },
    {
      keyColumn: 'vehicle_VM',
      key: 'descricaoTipoVeiculo',
      description: 'Veículo',
    },
    { keyColumn: 'period_date', key: 'dataInicio', description: 'Período' },
    { keyColumn: 'cost', key: 'custoFormatado', description: 'Custo' },
    { keyColumn: 'action', key: {}, description: 'Ações' },
  ]

  displayedColumns: string[] = [
    'location_OD',
    'servico_TS',
    'vehicle_VM',
    'period_date',
    'cost',
    'action',
  ]

  ngOnInit(): void {
    this.loadItems()
  }

  get itemIds(): number[] {
    return ((this.item.items ?? []) as any[])
      .map((i: any) => i.itemPropostaId)
      .filter((v: any) => !!v)
  }

  close() {
    if (this.changed) {
      this.response.emit(true)
    }
    this.dialogRef.close()
  }

  // Fonte única: a lista de itens da proposta (ItemProposta/proposta) já traz
  // todos os campos usados na tabela, inclusive `observacao` — o detalhe por id
  // (getProposalItembyId) não retorna observação de forma confiável.
  loadItems() {
    const ids = this.itemIds
    const propostaId = this.item.propostaId
    if (!ids.length || !propostaId) {
      this.rows = []
      return
    }
    this.loading = true
    const idSet = new Set(ids.map((id) => Number(id)))
    this.proposalItemService
      .listPagedProposalItemByProposal(propostaId, 1, 999)
      .subscribe({
        next: (response: any) => {
          const pending = ((response?.data ?? []) as any[]).filter((it) =>
            idSet.has(Number(it.id)),
          )
          if (!pending.length) {
            this.rows = []
            this.loading = false
            return
          }
          forkJoin(pending.map((it) => this.buildRow(it))).subscribe({
            next: (rows) => {
              this.rows = rows.filter((r) => !!r)
            },
            error: () => {
              this.snackbar.error(
                'Não foi possível carregar os itens da proposta.',
              )
            },
            complete: () => {
              this.loading = false
            },
          })
        },
        error: () => {
          this.snackbar.error('Não foi possível carregar os itens da proposta.')
          this.loading = false
        },
      })
  }

  // Anexa as paradas (trechos 'P') do itinerário a cada item já detalhado.
  buildRow(it: any) {
    return this.itinerariesService
      .listPagedItinerariesStopByItemPropose(it.id, 1, 999)
      .pipe(
        catchError(() => of({ data: [] } as any)),
        map((stops: any) => {
          const paradas = ((stops?.data ?? []) as any[])
            .filter((p) => (p.trecho ?? 'P') === 'P')
            .map((p, idx) => ({ ...p, ordem: p.ordem ?? idx + 1 }))
            .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
          return {
            ...it,
            itemPropostaId: it.id,
            paradas,
            custoFormatado: `R$ ${convertCurrency(it.custo)}`,
          }
        }),
      )
  }

  applyDiscount(row: any) {
    const dialogRef = this.dialog.open(ModalPurchasesDiscountApplyComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '1000px',
      maxWidth: '1000px',
      maxHeight: '620px',
      data: { itemPropostaId: row.itemPropostaId },
    })
    dialogRef.componentInstance.response.subscribe((response) => {
      if (response) {
        this.changed = true
        this.removeRow(row.itemPropostaId)
      }
    })
  }

  viewItineraries(row: any) {
    this.dialog.open(ModalPurchasesDiscountItinerariesComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '1000px',
      maxWidth: '1000px',
      maxHeight: '90vh',
      data: {
        itemPropostaId: row.itemPropostaId,
        propostaId: row.propostaId ?? this.item.propostaId,
        item: row,
      },
    })
  }

  // Ao aplicar o desconto o item deixa de estar pendente; some da lista. Se não
  // sobrar nenhum, avisa a listagem para recarregar e fecha a tela.
  removeRow(id: number) {
    this.rows = this.rows.filter((r) => r.itemPropostaId !== id)
    if (!this.rows.length) {
      this.response.emit(true)
      this.dialogRef.close()
    }
  }
}
